import React, { useCallback, useEffect, useState } from 'react'
import NowMap from './NowMap'
import AuthScreen from './components/AuthScreen'
import { PALETTES, TONE, BODY_FONT } from './lib/palette'
import { getSession, signIn, register, signOut, isUnauthorized } from './lib/api'

// The service worker keeps a runtime cache of the reflections response. Newer builds no longer
// register that route, but a device that ran an older build still holds the cache, and leaving
// it in place would show one account's reflections to the next person to sign in on this device
// while offline. The precached app shell is deliberately left alone — it is not account data,
// and deleting it would force a full re-download on every sign-out.
const clearCachedAccountData = async () => {
    if (typeof window === 'undefined' || !window.caches) return
    try {
        await window.caches.delete('api-reflections')
    } catch (err) {
        console.error('Failed to clear cached reflections:', err)
    }
}

const App = () => {
    // 'loading' until the cookie has been checked. Rendering the sign-in screen first and
    // replacing it a moment later would flash it at everyone who is already signed in.
    const [status, setStatus] = useState('loading')
    const [user, setUser] = useState(null)
    const [mode, setMode] = useState('signin')
    const [authError, setAuthError] = useState(null)
    const [busy, setBusy] = useState(false)

    const palette = PALETTES[TONE]

    useEffect(() => {
        let cancelled = false
        getSession()
            .then(found => {
                if (cancelled) return
                setUser(found)
                setStatus('authed')
            })
            .catch(err => {
                if (cancelled) return
                // Not being signed in is the ordinary case, not a fault worth logging.
                if (!isUnauthorized(err)) console.error('Failed to check session:', err)
                setStatus('anon')
            })
        return () => {
            cancelled = true
        }
    }, [])

    const submitCredentials = async values => {
        setBusy(true)
        setAuthError(null)
        try {
            const found = mode === 'register' ? await register(values) : await signIn(values)
            setUser(found)
            setStatus('authed')
        } catch (err) {
            setAuthError(err.message)
        } finally {
            setBusy(false)
        }
    }

    const changeMode = nextMode => {
        setMode(nextMode)
        // The previous screen's complaint does not apply to this one.
        setAuthError(null)
    }

    const endSession = useCallback(async () => {
        setUser(null)
        setStatus('anon')
        setMode('signin')
        setAuthError(null)
        await clearCachedAccountData()
    }, [])

    const handleSignOut = useCallback(async () => {
        try {
            await signOut()
        } catch (err) {
            // The server may already have dropped the session. Either way the local state has to
            // end up signed out — refusing to sign out because signing out failed is the one
            // outcome nobody wants.
            console.error('Failed to sign out cleanly:', err)
        }
        await endSession()
    }, [endSession])

    if (status === 'loading') {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    background: palette.bg,
                    color: palette.sub,
                    fontFamily: BODY_FONT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                Loading…
            </div>
        )
    }

    if (status === 'anon') {
        return (
            <AuthScreen
                palette={palette}
                mode={mode}
                onModeChange={changeMode}
                onSubmit={submitCredentials}
                error={authError}
                busy={busy}
            />
        )
    }

    return <NowMap user={user} onSignOut={handleSignOut} onSessionExpired={endSession} />
}

export default App
